import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ListTree } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle, Pill } from "@/components/CrudPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DestinationPicker, { invalidateDestinations } from "@/components/DestinationPicker";

const ACTIONS = ["extension", "ring_group", "queue", "ivr", "voicemail", "department", "hangup", "repeat"];
const empty = { name: "", description: "", extension: "", greeting: "", timeout: 10, options: [], timeout_destination_type: "hangup", invalid_destination_type: "repeat" };

export default function IVRs() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/ivrs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };
  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const addOption = () => setF("options", [...(form.options || []), { digit: "", action_type: "extension", action_id: "" }]);
  const setOpt = (i, k, v) => setF("options", form.options.map((o, idx) => idx === i ? { ...o, [k]: v } : o));
  const delOpt = (i) => setF("options", form.options.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { ...form, timeout: Number(form.timeout || 10) };
      if (editing) await api.put(`/ivrs/${editing.id}`, payload);
      else await api.post("/ivrs", payload);
      invalidateDestinations();
      toast.success(editing ? "Saved" : "IVR created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (it) => {
    if (!window.confirm(`Delete IVR "${it.name}"?`)) return;
    await api.delete(`/ivrs/${it.id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <PageTitle
        title="IVR / Auto-Attendant"
        subtitle="Build multi-level phone menus. “Press 1 for Sales, 2 for Support” — route each key to any destination."
        actions={
          <Button onClick={openAdd} data-testid="ivr-add-btn"
            className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-hover))] font-semibold">
            <Plus size={16} className="mr-1.5" /> New IVR
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ivr-list">
        {items.length === 0 && <div className="text-muted-foreground col-span-full py-12 text-center">No IVR menus yet.</div>}
        {items.map((it) => (
          <div key={it.id} className="rounded-xl border border-border bg-card p-5 card-hover" data-testid={`ivr-card-${it.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg grid place-items-center bg-[hsl(var(--gold)/0.12)] text-[hsl(var(--gold))]"><ListTree size={18} /></div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(it)} data-testid={`ivr-edit-${it.id}`}><Pencil size={15} /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-destructive" onClick={() => del(it)} data-testid={`ivr-delete-${it.id}`}><Trash2 size={15} /></Button>
              </div>
            </div>
            <h3 className="font-display font-semibold mt-3">{it.name}</h3>
            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{it.greeting || it.description || "No greeting set"}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(it.options || []).map((o, i) => (
                <Pill key={i} tone="gold">{o.digit} → {o.action_type}</Pill>
              ))}
              {(it.options || []).length === 0 && <span className="text-xs text-muted-foreground">No options</span>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto bg-card border-border" data-testid="ivr-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit IVR" : "New IVR"}</DialogTitle>
            <DialogDescription className="sr-only">Configure IVR greeting and menu options.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-muted-foreground">Name *</Label>
              <Input value={form.name} onChange={(e) => setF("name", e.target.value)} className="bg-secondary/40 border-border mt-1" data-testid="ivr-name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Internal extension (dial this number to reach the IVR)</Label>
              <Input value={form.extension} onChange={(e) => setF("extension", e.target.value)} className="bg-secondary/40 border-border mt-1"
                placeholder="e.g. 500" data-testid="ivr-extension" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Greeting (text-to-speech / prompt)</Label>
              <Textarea value={form.greeting} onChange={(e) => setF("greeting", e.target.value)} className="bg-secondary/40 border-border mt-1"
                placeholder="Welcome to InContact. Press 1 for Sales…" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Menu Options</Label>
              <Button size="sm" variant="outline" onClick={addOption} data-testid="ivr-add-option"><Plus size={14} className="mr-1" />Add key</Button>
            </div>
            <div className="space-y-2">
              {(form.options || []).map((o, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input value={o.digit} onChange={(e) => setOpt(i, "digit", e.target.value)} placeholder="#" className="w-14 bg-secondary/40 border-border text-center" data-testid={`ivr-opt-digit-${i}`} />
                  <Select value={o.action_type} onValueChange={(v) => setOpt(i, "action_type", v)}>
                    <SelectTrigger className="bg-secondary/40 border-border w-36" data-testid={`ivr-opt-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex-1">
                    <DestinationPicker type={o.action_type} value={o.action_id}
                      onChange={(v) => setOpt(i, "action_id", v)} testid={`ivr-opt-target-${i}`} />
                  </div>
                  <Button size="icon" variant="ghost" className="h-9 w-9 hover:text-destructive" onClick={() => delOpt(i)}><Trash2 size={15} /></Button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Timeout (s)</Label>
                <Input type="number" value={form.timeout} onChange={(e) => setF("timeout", e.target.value)} className="bg-secondary/40 border-border mt-1" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">On timeout</Label>
                <Select value={form.timeout_destination_type} onValueChange={(v) => setF("timeout_destination_type", v)}>
                  <SelectTrigger className="bg-secondary/40 border-border mt-1" data-testid="ivr-timeout-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-muted-foreground">Timeout target</Label>
                <div className="mt-1">
                  <DestinationPicker type={form.timeout_destination_type} value={form.timeout_destination_id}
                    onChange={(v) => setF("timeout_destination_id", v)} testid="ivr-timeout-target" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">On invalid key</Label>
                <Select value={form.invalid_destination_type} onValueChange={(v) => setF("invalid_destination_type", v)}>
                  <SelectTrigger className="bg-secondary/40 border-border mt-1" data-testid="ivr-invalid-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Invalid target</Label>
                <div className="mt-1">
                  <DestinationPicker type={form.invalid_destination_type} value={form.invalid_destination_id}
                    onChange={(v) => setF("invalid_destination_id", v)} testid="ivr-invalid-target" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="ivr-save-btn"
              className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-hover))] font-semibold">
              {saving ? "Saving…" : editing ? "Save Changes" : "Create IVR"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
